import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import GridWrapper from '../../../shared/components/layout/GridWrapper';
import Header from '../../../shared/components/layout/Header';
import Layout from '../../components/layout/AppLayout';
import Search from '../../../shared/components/ui/Search';
import { transformData } from '../../../super-admin/utils/transformData';
import Button from '../../../shared/components/ui/Button';
import Modal from '../../../shared/components/ui/Modal';
import toast from 'react-hot-toast';
import FormInput from '../../../shared/components/ui/FormInput';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  createClosedDeal,
  createUnderConsideration,
  getAllSalesUsersByProjectId,
  getFollowUpsByProjectId,
  updateFollowUp,
  updateLead,
  getAllAccommodationsByProject,
  getStageQualificationRequired
} from '../../services/api/implementation/impl';
import Multiselect from 'multiselect-react-dropdown';
import Cookies from 'js-cookie';
import { useSidebar } from '../../../shared/contexts/SidebarContext';
import noDataIcon from '../../../shared/assets/svg/NoData.svg';
import DP from '../../../shared/assets/svg/noUser.svg';
import { useForm, Controller } from 'react-hook-form';
import mixpanel from 'mixpanel-browser';
import { ChevronDown, X } from 'lucide-react';
import { createPortal } from 'react-dom';
import axios from 'axios';
import { ClosedWonCloseDeal } from '../../components/forms/ClosedWonCloseDeal';
import { UnderConsiderationCreate } from '../../components/forms/UnderConsiderationCreate';
import { SelectLead } from '../../components/forms/SelectLead';
import LeadsAiSummaryBanner, {
  LeadAiScoreBadge,
  useLeadAiScores
} from '../../components/ai/LeadsAiSummaryBanner';
import StageQualificationModal from '../../components/forms/StageQualificationModal';
import {
  buildReturnTo,
  navigateWithReturn,
  useSmartBack
} from '../../../shared/navigation';
import {
  getPriorityLeadsFilters,
  buildPriorityLeadsSearch,
  stripOversizedFollowUpIds
} from './salesUrlParams';
import {
  isUpcomingContactStage,
  isValidUpcomingContactDate,
  requiresNextFollowUpFields,
  requiresClosureNote,
  closureNoteLabel,
  closureNotePlaceholder,
  upcomingContactDateLabel,
  upcomingContactDateRequiredToast,
  upcomingContactPastDateToast,
  upcomingAgendaLabel,
  upcomingAgendaPlaceholder,
  upcomingAgendaRequiredToast,
  upcomingContactHelperText
} from '../../utils/requiresNextFollowUpFields';

const Leads = () => {
  const location = useLocation();
  // Memoised because followUpIds is re-split into a fresh array on every parse:
  // an unstable identity here makes filterLeads → Leads → setFilteredData loop
  // the component forever, which is what stalls (and eventually kills) the tab.
  const {
    salesID,
    salesName,
    filterType,
    projectID,
    followUpIds,
    q: qFromUrl
  } = useMemo(() => getPriorityLeadsFilters(location), [location]);
  const [searchQuery, setSearchQuery] = useState(qFromUrl || '');
  const [filteredData, setFilteredData] = useState([]);
  const { collapsed } = useSidebar();
  const navigate = useNavigate();
  const { onBack } = useSmartBack({ fallback: '/builder/sales/?view=dashboard' });
  const userId = Cookies.get('userId');
  const designationLevel = Cookies.get('userDesignationLevel');
  const scoreByLeadId = useLeadAiScores(projectID);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedTask, setselectedTask] = useState();
  const [openDropdown, setOpenDropdown] = useState(null);
  const [typeHeaderRef, setTypeHeaderRef] = useState(null);
  const [typeDropdownRect, setTypeDropdownRect] = useState(null);
  const [selectedType, setSelectedType] = useState('');
  const [showUpdateFollowUpModal, setShowUpdateFollowUpModal] = useState(false);
  const [isSelectLeadModalOpen, setIsSelectLeadModalOpen] = useState(false);
  const [isCloseDealModalOpen, setIsCloseDealModalOpen] = useState(false);
  const [isUnderConsiderationModalOpen, setIsUnderConsiderationModalOpen] = useState(false);
  const [selectedCloseDealLead, setSelectedCloseDealLead] = useState(null);
  const [selectedUnderConsiderationLead, setSelectedUnderConsiderationLead] = useState(null);
  const [leadSelectionActionType, setLeadSelectionActionType] = useState(null); // 'closeDeal' or 'underConsideration'
  // Follow up modal states
  const [followUpDate, setFollowUpDate] = useState('');
  const [log, setLog] = useState('');
  const [stage, setStage] = useState('');
  const [agenda, setAgenda] = useState('');
  const [otherLog, setOtherLog] = useState('');
  const [uploadedImage, setUploadedImage] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [selectedFollowUpTask, setSelectedFollowUpTask] = useState(null);
  const [selectedStage, setSelectedStage] = useState('');
  const [showPossessionModal, setShowPossessionModal] = useState(false);
  const [possessionDate, setPossessionDate] = useState('');
  const [payload, setPayload] = useState(null);
  const [projectAccommodations, setProjectAccommodations] = useState([]);
  const [qualModalOpen, setQualModalOpen] = useState(false);
  const [qualSubmitting, setQualSubmitting] = useState(false);
  const [pendingQualAction, setPendingQualAction] = useState(null);
  const [wantStageQualification, setWantStageQualification] = useState(false);

  // Track page load

  // PortalDropdown component for type filter
  const PortalDropdown = ({ anchorRect, children, onClose }) => {
    if (!anchorRect) return null;
    return createPortal(
      <div
        className="absolute bg-white border rounded shadow z-[1000]"
        style={{
          position: 'fixed',
          left: anchorRect.left,
          top: anchorRect.bottom,
          minWidth: anchorRect.width,
          maxHeight: '250px',
          overflowY: 'auto'
        }}
        onClick={(e) => e.stopPropagation()}>
        {children}
        <div className="fixed inset-0 z-[-1]" onClick={onClose} />
      </div>,
      document.body
    );
  };

  // Track page view
  useEffect(() => {
    mixpanel.track('Leads Viewed', {
      user_id: userId,
      timestamp: new Date().toISOString()
    });
  }, [userId]);

  useEffect(() => {
    mixpanel.track('Leads Page Viewed', {
      user_id: userId,
      sales_id: salesID,
      sales_name: salesName,
      filter_type: filterType,
      page: 'Leads'
    });
  }, [userId, salesID, salesName, filterType]);

  const {
    data: followUps,
    refetch: refetchFollowUps,
    isFetching
  } = useQuery({
    queryKey: ['followUps', projectID, selectedType, salesID, filterType],
    queryFn: () =>
      getFollowUpsByProjectId(projectID, selectedType || null, {
        limit: 200,
        status: 'Active',
        sales_id: salesID || undefined,
        escalated: filterType === 'escalated' || filterType === 'Escalated' || undefined
      }),
    enabled: !!projectID,
    staleTime: 60_000,
    onSuccess: (data) => {
      mixpanel.track('Follow Ups API Success', {
        user_id: userId,
        total_follow_ups: data?.data?.length || 0,
        sales_id: salesID,
        filter_type: filterType,
        project_id: projectID
      });
    },
    onError: (error) => {
      mixpanel.track('Follow Ups API Failure', {
        user_id: userId,
        error_message: error?.message,
        sales_id: salesID,
        filter_type: filterType,
        project_id: projectID
      });
    }
  });

  const {
    register,
    formState: { errors },
    setValue,
    watch,
    control
  } = useForm();

  const SALESID = watch('salesUserID');

  // Stage options from mobile
  const stageOptions = [
    { id: 'Phone Call Initiated', title: 'Phone Call Initiated (Cold)' },
    { id: 'Approached', title: 'Approached (Cold)' },
    { id: 'Demo Scheduled', title: 'Site Visit Scheduled (Upcoming visit)' },
    { id: 'Presentation', title: 'Presentation (Warm)' },
    { id: 'Site Visited', title: 'Site Visited (Warm)' },
    { id: 'Paused / Nurture', title: 'Paused / Nurture (Inactive pipeline)' },
    { id: 'Future Prospect', title: 'Future Prospect (Contact later)' },
    { id: 'Commercials Shared', title: 'Commercials Shared (Hot)' },
    { id: 'Negotiation', title: 'Negotiation (Hot)' },
    { id: 'Under Consideration', title: 'Under Consideration (Confirmed)' },
    { id: 'Deal Closed', title: 'Deal Closed (Closed Won)' },
    { id: 'Deal Dropped', title: 'Deal Dropped (Closed Lost)' }
  ];

  // Log options from mobile
  const logOptions = [
    'Call Not Connected',
    'Switched Off',
    'Not Reachable',
    'Number Busy',
    'No Answer',
    'Wrong Number',
    'Call Connected (Not Interested)',
    'Call Connected (Interested)',
    'Call Connected (Call Back Later)',
    'Call Connected (Sent WhatsApp)',
    'Call Connected (Sent SMS)',
    'Call Connected (Shared Brochure)',
    'Call Connected (Meeting Scheduled)',
    'Demo Proposed',
    'Demo Scheduled',
    'Client Requested Demo',
    'Call Connected (Shared Pricing Info)',
    'Call Connected (Language Barrier)',
    'Call Connected (Decision Maker Not Available)',
    'Follow-up Tomorrow',
    'Follow-up in 3 Days',
    'Follow-up Next Week',
    'Follow-up After Site Visit',
    'Follow-up After Family Discussion',
    'Follow-up After Festival/Holiday',
    'Awaiting Callback from Customer',
    'Looking for Plot',
    'Looking for Flat',
    'Looking for Commercial',
    'Finalizing Options',
    'Just Exploring',
    'Budget Too Low',
    'Referred by Friend/Family',
    'Wants Site Visit',
    'Site Visit Done (Awaiting Feedback)',
    'Site Visit Done (Not Interested)',
    'Booked with Another Project',
    'Booked with Us',
    'Already Visited Office',
    'Project Not in Preferred Locality',
    'Other'
  ];

  useEffect(() => {
    refetchFollowUps();
  }, []);

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = () => {
      if (openDropdown) {
        setOpenDropdown(null);
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [openDropdown]);

  // Handler to open dropdown and set position
  const handleHeaderDropdownClick = (type, event) => {
    // If clicking the same dropdown that's already open, close it
    if (openDropdown === type) {
      setOpenDropdown(null);
      return;
    }

    if (type === 'type' && typeHeaderRef) {
      setTypeDropdownRect(typeHeaderRef.getBoundingClientRect());
    }
    setOpenDropdown(type);
  };

  // Handler to close dropdown
  const handleCloseDropdown = () => setOpenDropdown(null);

  // Handler to select type filter
  const handleSelectTypeFilter = (typeValue) => {
    setSelectedType(typeValue);
    setOpenDropdown(null);

    mixpanel.track('Type Filter Selected', {
      user_id: userId,
      selected_type: typeValue,
      filter_type: filterType
    });
  };

  const { data: allSales, refetch: refetchSales } = useQuery({
    queryKey: ['sales'],
    queryFn: () => getAllSalesUsersByProjectId(85, 1, 1000),
    onSuccess: (data) => {
      mixpanel.track('Sales Users API Success', {
        user_id: userId,
        total_sales_users: data?.data?.length || 0
      });
    },
    onError: (error) => {
      mixpanel.track('Sales Users API Failure', {
        user_id: userId,
        error_message: error?.message
      });
    }
    // enabled: !!projectID
  });

  // Fetch project accommodations based on selected follow-up task's project
  const { data: projectAccommodationsData } = useQuery({
    queryKey: ['project_accommodations', selectedFollowUpTask?.project?.id],
    queryFn: () => getAllAccommodationsByProject(selectedFollowUpTask?.project?.id),
    enabled: !!selectedFollowUpTask?.project?.id,
    onError: (error) => {
      console.error('Error fetching project accommodations:', error);
    }
  });

  // Prefill project accommodations from lead data when available
  useEffect(() => {
    if (
      selectedFollowUpTask?.lead?.accomodations &&
      Array.isArray(selectedFollowUpTask?.lead?.accomodations) &&
      projectAccommodationsData &&
      projectAccommodationsData.length > 0
    ) {
      const accommodationIds = selectedFollowUpTask.lead.accomodations;
      const initialAccommodations = projectAccommodationsData.filter((acc) =>
        accommodationIds.includes(acc.id)
      );
      setProjectAccommodations(initialAccommodations);
    } else if (selectedFollowUpTask && projectAccommodationsData) {
      // Reset if no accommodations in lead
      setProjectAccommodations([]);
    }
  }, [selectedFollowUpTask, projectAccommodationsData]);

  const lowLevelSalesOptions = allSales?.data
    ?.filter((item) => item.level === 'Low')
    .map((item) => ({
      label: item.name,
      value: item.id,
      id: item.id
    }));

  // Filter leads based on salesID and filterType
  const filterLeads = useCallback(() => {
    if (!followUps?.data) return [];

    let filtered = followUps?.data?.filter((item) => item.status === 'Active');

    // Filter by sales ID if provided (URL params are strings; API ids are numbers)
    if (salesID != null && salesID !== '') {
      const salesIdNum = Number(salesID);
      filtered = filtered?.filter(
        (item) =>
          Number(item.user?.id) === salesIdNum ||
          Number(item.assignedto) === salesIdNum ||
          Number(item.sales_id) === salesIdNum
      );
    }

    // Apply additional filters based on filterType
    switch (filterType) {
      case 'Payment':
        filtered = filtered.filter((item) => item.type === 'Payment');
        break;
      case 'Default':
        filtered = filtered.filter((item) => item.type === 'Default');
        break;
      case 'ClosedDeal Details':
        filtered = filtered.filter((item) => item.type === 'ClosedDeal Details');
        break;
      case 'UnderConsideration Details':
        filtered = filtered.filter((item) => item.type === 'UnderConsideration Details');
        break;
      case 'Possession Date':
        filtered = filtered.filter((item) => item.type === 'Posession Date');
        break;
      case 'Absent':
        filtered = filtered.filter((item) => item.isAbsent);
        break;
      case 'Escalated':
        filtered = filtered.filter((item) => {
          if (!item.isEscalated) return false;
          const leadStatus = item?.lead?.status;
          const leadPstatus = item?.lead?.pstatus;
          const stage = item?.stage;
          if (leadStatus === 'Closed') return false;
          if (leadPstatus === 'Closed Won' || leadPstatus === 'Closed Lost') return false;
          if (stage === 'Deal Closed' || stage === 'Closed Won') return false;
          if (stage === 'Deal Dropped' || stage === 'Deal Cancelled') return false;
          return true;
        });
        break;
      default:
        break;
    }

    // Action Center card click: show exactly the follow-ups behind that count
    if (Array.isArray(followUpIds) && followUpIds.length > 0) {
      const idSet = new Set(followUpIds.map((id) => Number(id)));
      filtered = filtered.filter((item) => idSet.has(Number(item.id)));
    }

    // Track filter results
    mixpanel.track('Leads Filtered', {
      user_id: userId,
      filter_type: filterType,
      sales_id: salesID,
      selected_type: selectedType,
      follow_up_ids_count: Array.isArray(followUpIds) ? followUpIds.length : 0,
      total_filtered_leads: filtered.length,
      original_count: followUps?.data?.length
    });

    return filtered;
  }, [followUps, salesID, filterType, selectedType, followUpIds, userId]);

  const Leads = useMemo(() => filterLeads(), [filterLeads]);

  useEffect(() => {
    setFilteredData(Leads);
  }, [Leads]);

  function formatDate(dateString) {
    if (!dateString) return '---';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return '---';
    const options = { day: '2-digit', month: 'short', year: 'numeric' };
    return date.toLocaleDateString('en-GB', options);
  }

  const leadsFormat = [
    'name',
    'phone_number',
    'project',
    'agenda',
    'follow_up_date',
    'note',
    'type',
    'assigned_to',
    'actions',
    'id',
    'lead_id'
  ];

  const leadsData = filteredData?.map((el) =>
    transformData(
      {
        ...el.user,
        // Report Review and similar follow-ups carry no lead, so there is no contact to show
        name: el?.lead?.user?.name || el?.title || '---',
        phone_number: el?.lead?.user?.phone_number || '---',
        project: el?.project?.name || '---',
        agenda: el?.agenda || '---',
        follow_up_date: formatDate(el?.nextFollowUpDate) || '---',
        note: el?.notes || '---',
        assigned_to: el?.user?.name,
        actions: `Actions---${el.id}---${el?.sales_id || ''}`,
        id: el?.id,
        lead_id: el?.lead_id,
        type: el?.type
      },
      leadsFormat
    )
  );

  const handleSearchChange = useCallback(
    (incomingQuery) => {
      mixpanel.track('Lead Search Started', {
        user_id: userId,
        search_query: incomingQuery,
        filter_type: filterType,
        total_leads: Leads?.length || 0
      });

      setSearchQuery(incomingQuery || '');
      try {
        const params = buildPriorityLeadsSearch({
          salesID,
          salesName,
          filterType,
          projectID,
          followUpIds,
          q: incomingQuery || undefined
        });
        navigate(
          {
            pathname: location.pathname,
            search: `?${params.toString()}`
          },
          { replace: true, state: location.state }
        );
      } catch {
        // ignore
      }

      if (incomingQuery) {
        const lowerCaseQuery = incomingQuery.toLowerCase();
        const filteredLeads = Leads?.filter((lead) => {
          const user = lead.user;
          return (
            (user &&
              (user.name?.toLowerCase().includes(lowerCaseQuery) ||
                user.project?.toLowerCase().includes(lowerCaseQuery) ||
                user.phone_number?.toLowerCase().includes(lowerCaseQuery) ||
                user.assigned_to?.toLowerCase().includes(lowerCaseQuery))) ||
            lead.reference?.toLowerCase().includes(lowerCaseQuery)
          );
        });
        setFilteredData(filteredLeads);

        mixpanel.track('Lead Search Completed', {
          user_id: userId,
          search_query: incomingQuery,
          filter_type: filterType,
          search_results: filteredLeads?.length || 0,
          total_leads: Leads?.length || 0
        });
      } else {
        setFilteredData(Leads);
        mixpanel.track('Lead Search Cleared', {
          user_id: userId,
          filter_type: filterType
        });
      }
    },
    [
      Leads,
      userId,
      filterType,
      salesID,
      salesName,
      projectID,
      followUpIds,
      navigate,
      location.pathname,
      location.state
    ]
  );

  const handleOnClickSearch = () => {
    mixpanel.track('Search Button Clicked', {
      user_id: userId,
      search_query: searchQuery,
      filter_type: filterType
    });

    const filtered = Leads?.filter((item) =>
      item.user?.name.toLowerCase().includes(searchQuery.toLowerCase())
    );
    setFilteredData(filtered);
    setSearchQuery('');

    mixpanel.track('Search Results Displayed', {
      user_id: userId,
      search_query: searchQuery,
      filter_type: filterType,
      results_count: filtered?.length || 0
    });
  };

  const handleAssign = () => {
    mixpanel.track('Assign User Button Clicked', {
      user_id: userId,
      selected_task_id: selectedTask,
      selected_sales_id: SALESID,
      filter_type: filterType
    });

    if (SALESID) {
      const payload = {
        assignedto: Number(SALESID)
      };
      updateFollowup(payload);
    }
  };

  const { mutate: updateFollowup } = useMutation({
    mutationFn: (payload) => updateFollowUp(selectedTask, payload),
    onMutate: () => {
      setIsSubmitting(true); // Disable the button when mutation starts
      mixpanel.track('Update Follow Up Started', {
        user_id: userId,
        task_id: selectedTask
      });
    },
    onSuccess: async (result) => {
      // Check if result is an error object (API now returns errors instead of throwing)
      if (
        result?.response?.status ||
        result?.message?.includes('Error') ||
        result?.name === 'AxiosError'
      ) {
        // Handle as error
        setIsSubmitting(false);
        console.error('Error assigning user', result);
        toast.error(result?.response?.data?.message || result?.message || 'Error assigning user');

        mixpanel.track('Update Follow Up Failure', {
          user_id: userId,
          task_id: selectedTask,
          error_message: result?.response?.data?.message || result?.message,
          filter_type: filterType
        });
        return;
      }

      // Handle as success
      setIsSubmitting(false);
      toast.success('User Assigned Successfully');
      setShowConfirmationModal(false);
      setselectedTask();
      await refetchFollowUps();

      mixpanel.track('Update Follow Up Success', {
        user_id: userId,
        task_id: selectedTask,
        assigned_to: SALESID,
        filter_type: filterType
      });
    },
    onError: (err) => {
      setIsSubmitting(false);
      console.error('Error assigning user', err);
      toast.error(err?.response?.data?.message || err?.message || 'Error assigning user');

      mixpanel.track('Update Follow Up Failure', {
        user_id: userId,
        task_id: selectedTask,
        error_message: err?.response?.data?.message || err?.message,
        filter_type: filterType
      });
    }
  });

  // Handle lead row click
  const handleLeadClick = (leadId) => {
    // Report Review and other lead-less follow-ups have nowhere to drill into
    if (leadId == null || leadId === '') return;
    mixpanel.track('Lead Row Clicked', {
      user_id: userId,
      lead_id: leadId,
      filter_type: filterType
    });
    navigateWithReturn(navigate, `/builder/sales/${leadId}`, {
      // A stale oversized followUpIds would be re-encoded into ?returnTo=, doubling the URL
      returnTo: buildReturnTo(
        location.pathname,
        stripOversizedFollowUpIds(location.search || '')
      )
    });
  };

  // Handle assign modal open
  const handleAssignModalOpen = (taskId) => {
    mixpanel.track('Assign Modal Opened', {
      user_id: userId,
      task_id: taskId,
      filter_type: filterType
    });
    setselectedTask(taskId);
    setShowConfirmationModal(true);
  };

  // Handle modal close
  const handleModalClose = () => {
    mixpanel.track('Assign Modal Closed', {
      user_id: userId,
      task_id: selectedTask,
      filter_type: filterType
    });
    setShowConfirmationModal(false);
  };

  // Handle cancel button
  const handleCancelAssign = () => {
    mixpanel.track('Assign Cancelled', {
      user_id: userId,
      task_id: selectedTask,
      filter_type: filterType
    });
    setShowConfirmationModal(false);
  };

  // Reset Follow Up Form
  const resetFollowUpForm = () => {
    setAgenda('');
    setLog('');
    setFollowUpDate('');
    setStage('');
    setOtherLog('');
    setUploadedImage(null);
    setPossessionDate('');
    setProjectAccommodations([]);
    setValue('followUpStage', '');
    setWantStageQualification(false);
  };

  // Handle file upload
  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    try {
      setUploading(true);
      const token = Cookies.get('token');
      if (!token) throw new Error('No token found');

      const formData = new FormData();
      formData.append('files', file);

      const res = await axios.post('https://application.housingram.com/api/s3/upload', formData, {
        headers: {
          authorization: token,
          'Content-Type': 'multipart/form-data'
        }
      });

      const uploaded = res.data.data.files[0];
      setUploadedImage({
        name: uploaded.originalName,
        url: uploaded.presignedUrl
      });
      toast.success('File uploaded successfully');
    } catch (error) {
      console.error('File upload error:', error);
      toast.error('Failed to upload file');
    } finally {
      setUploading(false);
    }
  };

  // Handle Complete Follow up
  const executeFollowUpCompleteLeads = async (stageQualification) => {
    const needsNextFollowUp = requiresNextFollowUpFields({ stage, log });
    const noteText = (agenda || '').trim();
    const payload = {
      agenda: noteText,
      nextFollowUpDate: needsNextFollowUp ? followUpDate : null,
      log: log === 'Other' ? otherLog : log,
      lead_id: selectedFollowUpTask?.lead_id,
      status: 'Inactive',
      name: stage,
      attachments: uploadedImage ? [{ name: uploadedImage.name, url: uploadedImage.url }] : [],
      // Closure reason belongs on the stage activity so Stage History shows why.
      ...(!needsNextFollowUp && noteText ? { description: noteText } : {}),
      ...(stageQualification ? { stageQualification } : {})
    };

    setIsSubmitting(true);
    await updateFollowUp(selectedFollowUpTask?.id, payload);

    if (
      stage === 'Commercials Shared' ||
      stage === 'Negotiation' ||
      selectedFollowUpTask?.stage === 'Commercials Shared' ||
      selectedFollowUpTask?.stage === 'Negotiation'
    ) {
      try {
        const leadPayload = {
          accomodations: projectAccommodations.map((item) => item.id || item)
        };
        await updateLead(selectedFollowUpTask?.lead_id, leadPayload);
      } catch (leadError) {
        console.error('Failed to update lead project accommodation:', leadError);
      }
    }

    toast.success('Follow Up updated successfully');
    await refetchFollowUps();
    setShowUpdateFollowUpModal(false);
    resetFollowUpForm();
    setIsSubmitting(false);
  };

  const openQualificationIfRequiredLeads = async (targetStage, onQualified, optedIn = false) => {
    const leadId = selectedFollowUpTask?.lead_id;
    if (!leadId) {
      await onQualified(null);
      return;
    }
    if (!optedIn) {
      await onQualified(null);
      return;
    }
    try {
      const required = await getStageQualificationRequired(leadId, targetStage);
      if (required?.required && (required?.mandatory?.length ?? 0) > 0) {
        setPendingQualAction({
          transition: required.transition,
          targetStage,
          onQualified
        });
        setQualModalOpen(true);
        return;
      }
      if (optedIn && required?.transition && !(required?.mandatory?.length > 0)) {
        toast.error(
          'No qualification questions configured. Add them from Leads → Qualification questions.'
        );
      }
      await onQualified(null);
    } catch (error) {
      if (error?.response?.status === 404 || error?.response?.status >= 500) {
        console.warn('Stage qualification API unavailable — proceeding without gate', error);
        toast.error('Qualification service unavailable — follow-up saved without gate');
        await onQualified(null);
        return;
      }
      throw error;
    }
  };

  const handleQualificationSubmitLeads = async (stageQualification) => {
    if (!pendingQualAction?.onQualified) return;
    try {
      setQualSubmitting(true);
      await pendingQualAction.onQualified(stageQualification);
      setQualModalOpen(false);
      setPendingQualAction(null);
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Follow-up update failed');
    } finally {
      setQualSubmitting(false);
    }
  };

  const handleCompleteFollowup = async () => {
    if (!stage) {
      toast.error('Select Stage');
      return;
    }

    if (!log) {
      toast.error('Select/Enter log');
      return;
    }

    if (log === 'Other' && !otherLog) {
      toast.error('Enter log');
      return;
    }

    const needsNextFollowUp = requiresNextFollowUpFields({ stage, log });

    if (needsNextFollowUp && !followUpDate) {
      toast.error(upcomingContactDateRequiredToast(stage));
      return;
    }

    if (
      isUpcomingContactStage(stage) &&
      !isValidUpcomingContactDate(followUpDate)
    ) {
      toast.error(upcomingContactPastDateToast(stage));
      return;
    }

    if (needsNextFollowUp && !agenda) {
      toast.error(upcomingAgendaRequiredToast(stage));
      return;
    }

    if (requiresClosureNote({ stage, log }) && !agenda?.trim()) {
      toast.error(`Enter ${closureNoteLabel({ stage, log }).toLowerCase()}`);
      return;
    }

    // Validate accommodation for specific stages
    if (
      (stage === 'Commercials Shared' ||
        stage === 'Negotiation' ||
        selectedFollowUpTask?.stage === 'Commercials Shared' ||
        selectedFollowUpTask?.stage === 'Negotiation') &&
      projectAccommodations.length === 0
    ) {
      toast.error('Please select at least one project accommodation');
      return;
    }

    const noteText = (agenda || '').trim();
    const payload = {
      agenda: noteText,
      nextFollowUpDate: needsNextFollowUp ? followUpDate : null,
      log: log === 'Other' ? otherLog : log,
      lead_id: selectedFollowUpTask?.lead_id,
      status: 'Inactive',
      name: stage,
      attachments: uploadedImage ? [{ name: uploadedImage.name, url: uploadedImage.url }] : [],
      ...(!needsNextFollowUp && noteText ? { description: noteText } : {})
    };

    try {
      // Navigate if needed
      if (selectedStage && selectedStage === 'Under Consideration') {
        setPayload(payload);
        setIsUnderConsiderationModalOpen(true);
        setShowUpdateFollowUpModal(false);
        return;
      }

      if (selectedStage && selectedStage === 'Deal Closed') {
        try {
          setIsSubmitting(true);
          await updateFollowUp(selectedFollowUpTask?.id, payload);
        } catch (error) {
          toast.error(
            error?.response?.data?.message || error?.message || 'Failed to start close deal'
          );
          setIsSubmitting(false);
          return;
        }
        setIsSubmitting(false);
        setPayload(payload);
        setIsSelectLeadModalOpen(true);
        setShowUpdateFollowUpModal(false);
        return;
      }
      try {
        await openQualificationIfRequiredLeads(stage, executeFollowUpCompleteLeads, wantStageQualification);
      } catch (error) {
        console.error('Failed to update followup:', error);
        toast.error(error?.response?.data?.message || 'Failed to update followup');
      }
    } catch (error) {
      console.error('Failed to update followup:', error);
      toast.error(error?.response?.data?.message || 'Failed to update followup');
    }
  };

  const handlePossessionDateSubmit = async () => {
    if (!possessionDate) {
      toast.error('Please select a possession date');
      return;
    }

    const payload = {
      posessionDate: possessionDate, // Note: using "posession" to match mobile/backend
      lead_id: selectedFollowUpTask?.lead_id,
      status: 'Inactive'
    };

    console.log('Possession Date Payload', JSON.stringify(payload, null, 2));

    try {
      setIsSubmitting(true);
      await updateFollowUp(selectedFollowUpTask?.id, payload);
      toast.success('Possession Date updated successfully');
      await refetchFollowUps();
      setShowPossessionModal(false);
      resetFollowUpForm();
    } catch (error) {
      console.error('Failed to update possession date:', error);
      toast.error(error?.response?.data?.message || 'Failed to update possession date');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleLeadSelect = (lead) => {
    setIsSelectLeadModalOpen(false);
    if (leadSelectionActionType === 'underConsideration') {
      setSelectedUnderConsiderationLead(lead);
      setIsUnderConsiderationModalOpen(true);
    } else {
      // Default to close deal
      setSelectedCloseDealLead(lead);
      setIsCloseDealModalOpen(true);
    }
    setLeadSelectionActionType(null);
  };

  const handleCloseDeal = (data) => {
    createClosedDealMutation(data);
  };

  const { mutate: createClosedDealMutation } = useMutation({
    mutationFn: (data) => createClosedDeal(data),
    onSuccess: async () => {
      toast.success('Deal closed successfully');
      setIsCloseDealModalOpen(false);
      const closedLeadId = selectedCloseDealLead?.id;

      if (payload) {
        await updateFollowUp(selectedFollowUpTask?.id, payload);
        resetFollowUpForm();
      } else {
        await updateFollowUp(selectedFollowUpTask?.id, { status: 'Inactive' });
      }
      setSelectedFollowUpTask(null);
      setSelectedCloseDealLead(null);
      setPayload(null);
      refetchFollowUps();
      if (closedLeadId) {
        navigate(`/builder/deals/${closedLeadId}`, { state: { status: 'Closed Won' } });
      }
    },
    onError: (error) => {
      toast.error(error.response.data.message || error?.message || 'Failed to close deal');
    }
  });

  const handleUnderConsideration = (data) => {
    createUnderConsiderationMutation(data);
  };

  const { mutate: createUnderConsiderationMutation } = useMutation({
    mutationFn: (data) => createUnderConsideration(data),
    onSuccess: async () => {
      toast.success('Under Consideration created successfully');
      setIsUnderConsiderationModalOpen(false);
      setSelectedUnderConsiderationLead(null);
      if (payload) {
        await updateFollowUp(selectedFollowUpTask?.id, payload);
        resetFollowUpForm();
      } else {
        await updateFollowUp(selectedFollowUpTask?.id, { status: 'Inactive' });
      }
      setSelectedFollowUpTask(null);
      setPayload(null);
      refetchFollowUps();
    },
    onError: (error) => {
      toast.error(
        error?.response?.data?.message || error?.message || 'Failed to create under consideration'
      );
    }
  });

  return (
    <Layout>
      {showConfirmationModal && (
        <Modal isDirty confirmOnClose width={45} name="Assign Sales" onClose={handleModalClose}>
          <div className="flex flex-col justify-start items-center z-10">
            <div className="flex flex-row w-full">
              <FormInput
                width={50}
                label={''}
                register={register}
                register_key={'salesUserID'}
                isRequired={false}
                type={'dropdown'}
                options={[
                  { id: '', label: 'select user', value: '', selected: true, disabled: true },
                  ...lowLevelSalesOptions
                ]}
                validation={{ required: 'user is required' }}
                errors={errors}
              />
            </div>
            <div className="w-full flex justify-end items-center space-x-4 mt-8">
              <Button type="reset" onClick={handleCancelAssign} displayType="secondary">
                Cancel
              </Button>

              <Button disabled={isSubmitting} type="button" onClick={handleAssign}>
                Submit
              </Button>
            </div>
          </div>
        </Modal>
      )}

      {/* Follow Up Modal */}
      {showUpdateFollowUpModal && (
        <Modal
          isDirty
          confirmOnClose
          width={50}
          name="Follow Up"
          onClose={() => {
            setShowUpdateFollowUpModal(false);
            resetFollowUpForm();
          }}>
          <div className="flex flex-col gap-4">
            {/* Lead Info */}
            <div className="bg-gray-50 px-3 py-2.5 rounded-lg">
              <h3 className="font-semibold text-base text-gray-800 leading-snug">
                {selectedFollowUpTask?.type === 'Payment'
                  ? 'Payment Follow Up with '
                  : 'Follow Up with '}
                {selectedFollowUpTask?.lead?.user?.name || 'Lead'}
              </h3>
              <p className="text-xs text-gray-600 mt-0.5">
                Project: {selectedFollowUpTask?.project?.name || 'N/A'}
              </p>
            </div>

            {/* Stage Field */}
            <div className="w-full">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Stage <span className="text-red-500">*</span>
              </label>
              <Controller
                control={control}
                name="followUpStage"
                rules={{ required: 'Stage is required' }}
                render={({ field: { onChange, value } }) => (
                  <select
                    value={value || ''}
                    onChange={(e) => {
                      setSelectedStage(e.target.value);
                      setStage(e.target.value);
                      onChange(e.target.value);
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500">
                    <option value="" disabled>
                      Select Stage
                    </option>
                    {stageOptions.map((option) => (
                      <option key={option.id} value={option.id}>
                        {option.title}
                      </option>
                    ))}
                  </select>
                )}
              />
              {errors.followUpStage && (
                <p className="text-red-500 text-sm mt-1">{errors.followUpStage.message}</p>
              )}
            </div>

            {/* Log Field */}
            <div className="w-full">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Log <span className="text-red-500">*</span>
              </label>
              <select
                value={log}
                onChange={(e) => {
                  const nextLog = e.target.value;
                  setLog(nextLog);
                  if (nextLog !== 'Other') {
                    setOtherLog('');
                  }
                  if (!requiresNextFollowUpFields({ stage, log: nextLog })) {
                    setFollowUpDate('');
                    setAgenda('');
                  }
                }}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500">
                <option value="" disabled>
                  Select Log
                </option>
                {logOptions.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
              {log === 'Other' && (
                <input
                  type="text"
                  placeholder="Enter log here..."
                  value={otherLog}
                  onChange={(e) => setOtherLog(e.target.value)}
                  className="w-full px-3 py-2 mt-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              )}
            </div>

            {requiresNextFollowUpFields({ stage, log }) ? (
              <>
                <div className="w-full">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {upcomingContactDateLabel(stage)} <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="datetime-local"
                    value={followUpDate}
                    onChange={(e) => setFollowUpDate(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  {upcomingContactHelperText(stage) ? (
                    <p className="text-xs text-gray-500 mt-1">
                      {upcomingContactHelperText(stage)}
                    </p>
                  ) : null}
                </div>

                <div className="w-full">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {upcomingAgendaLabel(stage)} <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    placeholder={upcomingAgendaPlaceholder(stage)}
                    value={agenda}
                    onChange={(e) => setAgenda(e.target.value)}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </>
            ) : (
              <div className="w-full">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {closureNoteLabel({ stage, log })}{' '}
                  {requiresClosureNote({ stage, log }) && (
                    <span className="text-red-500">*</span>
                  )}
                </label>
                <textarea
                  placeholder={closureNotePlaceholder({ stage, log })}
                  value={agenda}
                  onChange={(e) => setAgenda(e.target.value)}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <p className="text-xs text-gray-500 mt-1">
                  No next follow-up is scheduled. This reason is saved to the lead&apos;s Stage
                  History &amp; logs.
                </p>
              </div>
            )}

            {/* Project Accommodation - only for current stage selection */}
            {(stage === 'Commercials Shared' || stage === 'Negotiation') && (
              <div className="w-full">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Project Accommodation <span className="text-red-500">*</span>
                </label>
                <Multiselect
                  options={projectAccommodationsData || []}
                  selectedValues={projectAccommodations}
                  onSelect={(selectedList) => setProjectAccommodations(selectedList)}
                  onRemove={(selectedList) => setProjectAccommodations(selectedList)}
                  displayValue="name"
                  placeholder={
                    selectedFollowUpTask?.project?.id
                      ? 'Select project accommodations'
                      : 'Select a project first'
                  }
                  disabled={!selectedFollowUpTask?.project?.id}
                  showCheckbox
                  style={{
                    multiselectContainer: { width: '100%' },
                    searchBox: {
                      minHeight: '40px',
                      fontSize: '1rem',
                      background: selectedFollowUpTask?.project?.id ? 'white' : '#f5f5f5'
                    },
                    chips: { fontSize: '0.875rem', padding: '0.25rem 0.5rem' }
                  }}
                />
              </div>
            )}

            {/* File Upload */}
            <div className="w-full">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Attachment (Optional)
              </label>
              <div className="flex items-center space-x-2">
                <label
                  htmlFor="file-upload"
                  className="cursor-pointer inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500">
                  <input
                    id="file-upload"
                    type="file"
                    className="sr-only"
                    onChange={handleFileUpload}
                    accept="image/*,.pdf,.doc,.docx"
                    disabled={uploading}
                  />
                  {uploading ? 'Uploading...' : 'Choose File'}
                </label>
                {uploadedImage && (
                  <div className="flex items-center space-x-2 text-sm text-gray-600">
                    <span className="truncate max-w-xs">{uploadedImage.name}</span>
                    <button
                      onClick={() => setUploadedImage(null)}
                      className="text-red-500 hover:text-red-700">
                      <X size={16} />
                    </button>
                  </div>
                )}
              </div>
            </div>

            <label className="flex cursor-pointer items-start gap-2 rounded-lg border border-gray-200 bg-gray-50 p-3">
              <input
                type="checkbox"
                className="mt-0.5"
                checked={wantStageQualification}
                onChange={(e) => setWantStageQualification(e.target.checked)}
              />
              <span className="text-sm text-gray-700">
                Complete qualification questions (optional)
                <span className="mt-0.5 block text-xs text-gray-500">
                  Uses questions set on the Leads list. Answers become filterable tags. Leave off to
                  update the stage directly.
                </span>
              </span>
            </label>

            {/* Action Buttons */}
            <div className="w-full flex justify-end items-center space-x-4 mt-6 pt-4 border-t">
              <Button
                type="reset"
                onClick={() => {
                  setShowUpdateFollowUpModal(false);
                  resetFollowUpForm();
                }}
                displayType="secondary">
                Cancel
              </Button>
              <Button disabled={isSubmitting} type="button" onClick={handleCompleteFollowup}>
                {isSubmitting ? 'Submitting...' : 'Submit'}
              </Button>
            </div>
          </div>
        </Modal>
      )}

      <div
        className={`fixed top-0 z-10 transition-all duration-300 bg-white ${
          collapsed ? 'left-[76px] w-[calc(100%-76px)]' : 'left-64 w-[calc(100%-16rem)]'
        }`}>
        <Header
          backArrow={true}
          onBack={onBack}
          data={{
            // Joined rather than interpolated: arriving with no filterType —
            // which any link that does not set one does, including the
            // notification digests — rendered the literal heading
            // "Follow Up null".
            name: filterType === 'Payment' || filterType === 'Default'
              ? 'Your Follow Up'
              : ['Follow Up', filterType, salesName ? `(${salesName})` : null]
                  .filter(Boolean)
                  .join(' ')
          }}
        />
      </div>
      <div className="w-full flex flex-col absolute z-5 bg-gray-100 pb-10 overflow-hidden">
        <div
          className={`flex items-center justify-between px-6 py-4 bg-white fixed top-[4rem] border-t-[2px] border-stone-100 shadow-sm z-[10] transition-all duration-300 ease-in-out ${
            collapsed ? 'left-[76px] right-0' : 'left-64 right-0'
          } mt-[0rem]`}>
          <Search onChange={handleSearchChange} query={searchQuery} onClick={handleOnClickSearch} />
        </div>

        <div className="px-6 pt-[8rem] pb-6">
          <LeadsAiSummaryBanner projectId={projectID} className="mb-6 mt-4" />

          <GridWrapper width={100} childrenCustomStyle="m-0">
            <div className="relative overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
            <div className="overflow-scroll hide-scrollbar relative rounded-t-lg">
              <table className="text-left w-full">
                <thead
                  className="bg-gray-200 !text-gray-600 w-full"
                  style={{ position: 'sticky', top: 0, left: 0, zIndex: 1 }}>
                  <tr className="font-normal h-[3rem] w-full">
                    <th className="px-4 py-2 border-b font-normal">Lead Name</th>
                    <th className="px-4 py-2 border-b font-normal">Phone</th>
                    <th className="px-4 py-2 border-b font-normal">Project</th>
                    <th className="px-4 py-2 border-b font-normal">Follow Up Agenda</th>
                    <th className="px-4 py-2 border-b relative font-normal">Follow Up Date</th>
                    <th className="px-4 py-2 border-b relative font-normal">Notes</th>
                    <th
                      className="px-4 py-2 border-b font-normal relative"
                      ref={(ref) => setTypeHeaderRef(ref)}>
                      <span>Type</span>
                      <button
                        className="ml-1 align-middle"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleHeaderDropdownClick('type', e);
                        }}>
                        <ChevronDown size={16} />
                      </button>
                      {openDropdown === 'type' && typeDropdownRect && (
                        <PortalDropdown anchorRect={typeDropdownRect} onClose={handleCloseDropdown}>
                          {[
                            {
                              value: '',
                              label: selectedType ? 'Clear Filter' : 'All Types'
                            },
                            {
                              value: 'Default',
                              label: 'Default'
                            },
                            {
                              value: 'Payment',
                              label: 'Payment'
                            },
                            {
                              value: 'Posession Date',
                              label: 'Possession Date'
                            },
                            {
                              value: 'ClosedDeal Details',
                              label: 'Closed Deal Details'
                            }
                          ].map((typeOption, idx) => (
                            <div
                              key={typeOption.value || idx}
                              className={`px-4 py-2 hover:bg-gray-100 cursor-pointer ${
                                typeOption.value === '' && 'bg-gray-100'
                              } ${typeOption.value === selectedType && 'bg-blue-100'}`}
                              onClick={() => handleSelectTypeFilter(typeOption.value)}>
                              {typeOption.label}
                            </div>
                          ))}
                        </PortalDropdown>
                      )}
                    </th>
                    <th className="px-4 py-2 border-b font-normal">Assigned</th>
                    <th className="px-4 py-2 border-b font-normal">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {leadsData && leadsData?.length > 0 ? (
                    leadsData?.map((task) => {
                      return (
                        <tr
                          key={task.id}
                          className={`border-b hover:bg-gray-50 bg-white h-[3.5rem] ${
                            task.lead_id ? 'cursor-pointer' : 'cursor-default'
                          }`}
                          onClick={() => handleLeadClick(task.lead_id)}>
                          <td className="px-4 py-2 max-w-[15rem]">
                            <div className="flex min-w-0 items-center gap-2">
                              <span className="truncate" title={task.name || undefined}>
                                {task.name}
                              </span>
                              <LeadAiScoreBadge
                                leadId={task.lead_id}
                                scoreByLeadId={scoreByLeadId}
                                aiScore={task.ai_score ?? task.lead?.ai_score}
                                aiCategory={task.ai_category ?? task.lead?.ai_category}
                              />
                            </div>
                          </td>
                          <td className="px-4 py-2">{task.phone_number}</td>
                          <td className="px-4 py-2">{task.project}</td>
                          <td className="px-4 py-2">{task.agenda || 0}</td>
                          <td className="px-4 py-2">{task.follow_up_date}</td>
                          <td className="px-4 py-2">{task.note}</td>
                          <td className="px-4 py-2">{task.type}</td>
                          <td className="px-4 py-2">
                            <div className="flex items-center justify-center w-full relative">
                              {filterType === 'Absent' ? (
                                <div
                                  className="flex gap-2 items-center"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleAssignModalOpen(task.id);
                                  }}>
                                  <img
                                    src={DP}
                                    alt=""
                                    className="cursor-pointer w-6 h-6 rounded-full"
                                  />
                                  <span>Unassigned</span>
                                </div>
                              ) : task.assigned_to ? (
                                <div className="flex cursor-pointer gap-1 justify-start items-center w-full">
                                  <div className="w-6 h-6 rounded-full bg-[#FFB346] text-white flex items-center justify-center text-[0.8rem] font-semibold border-2 border-white leading-none">
                                    {task.assigned_to?.charAt(0).toUpperCase()}
                                  </div>
                                  {task.assigned_to}
                                </div>
                              ) : (
                                <img
                                  src={DP}
                                  alt=""
                                  className="cursor-pointer w-6 h-6 rounded-full"
                                />
                              )}
                            </div>
                          </td>
                          <td className="px-4 py-2">
                            <Button
                              onClick={(e) => {
                                e.stopPropagation();
                                const fullTask = followUps?.data?.find((f) => f.id === task.id);
                                setSelectedFollowUpTask(fullTask);

                                // Check if this is a possession type task
                                if (fullTask?.type === 'Posession Date') {
                                  setShowPossessionModal(true);
                                } else if (fullTask?.type === 'ClosedDeal Details') {
                                  setLeadSelectionActionType('closeDeal');
                                  setIsSelectLeadModalOpen(true);
                                } else if (fullTask?.type === 'UnderConsideration Details') {
                                  setLeadSelectionActionType('underConsideration');
                                  setIsSelectLeadModalOpen(true);
                                } else {
                                  setShowUpdateFollowUpModal(true);
                                  // Pre-fill stage if it exists
                                  if (
                                    fullTask?.stage &&
                                    stageOptions?.find((item) => item.id === fullTask?.stage)
                                  ) {
                                    setStage(fullTask?.stage);
                                    setValue('followUpStage', fullTask?.stage);
                                  } else {
                                    setStage('');
                                    setValue('followUpStage', '');
                                  }

                                  // Prefill project accommodation data from lead
                                  // Note: This will be set after projectAccommodationsData is loaded
                                  // via useEffect below
                                  setProjectAccommodations([]);
                                }
                              }}>
                              Update
                            </Button>
                          </td>
                        </tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td colSpan={7} className="text-center py-7">
                        <div className="flex flex-col items-center justify-center">
                          <img
                            src={noDataIcon}
                            alt="No Data"
                            className="w-[25rem] h-[20rem] object-cover"
                          />
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </GridWrapper>
        </div>

        {/* Select Lead Modal */}
        {isSelectLeadModalOpen && (
          <Modal
            width={50}
            name="Select Lead"
            confirmOnClose={true}
            isDirty={true}
            onClose={() => {
              setIsSelectLeadModalOpen(false);
              setPayload(null);
            }}>
            <SelectLead
              onClose={() => {
                setIsSelectLeadModalOpen(false);
                setPayload(null);
              }}
              onLeadSelect={handleLeadSelect}
              isSubmitting={isSubmitting}
              projectName={selectedFollowUpTask?.project?.name}
              projectId={selectedFollowUpTask?.project?.id}
              filterForUnderConsideration={
                selectedFollowUpTask?.type === 'UnderConsideration Details'
              }
            />
          </Modal>
        )}

        {/* Close Deal Modal */}
        {isCloseDealModalOpen && (
          <Modal
            width={50}
            name="Close Deal"
            confirmOnClose={true}
            isDirty={true}
            onClose={() => {
              setIsCloseDealModalOpen(false);
              setSelectedCloseDealLead(null);
            }}>
            <ClosedWonCloseDeal
              onClose={() => {
                setIsCloseDealModalOpen(false);
                setSelectedCloseDealLead(null);
              }}
              onSubmit={handleCloseDeal}
              isSubmitting={isSubmitting}
              selectedLead={selectedCloseDealLead}
              skipFiltering={true}
              ucDetails={
                selectedFollowUpTask?.unit
                  ? {
                      project: selectedFollowUpTask?.unit?.project_id,
                      block: selectedFollowUpTask?.unit?.block_id,
                      level: selectedFollowUpTask?.unit?.level_id,
                      unit: selectedFollowUpTask?.unit_id
                    }
                  : { project: null, block: null, level: null, unit: null }
              }
            />
          </Modal>
        )}

        {isUnderConsiderationModalOpen && (
          <Modal
            width={50}
            name="Create Under Consideration"
            confirmOnClose={true}
            isDirty={true}
            onClose={() => {
              setIsUnderConsiderationModalOpen(false);
              setSelectedUnderConsiderationLead(null);
            }}>
            <UnderConsiderationCreate
              onClose={() => {
                setIsUnderConsiderationModalOpen(false);
                setSelectedUnderConsiderationLead(null);
              }}
              onSubmit={handleUnderConsideration}
              isSubmitting={isSubmitting}
              selectedLead={selectedUnderConsiderationLead?.id || selectedFollowUpTask?.lead_id}
              skipFiltering={true}
              ucDetails={
                selectedFollowUpTask?.unit
                  ? {
                      project: selectedFollowUpTask?.unit?.project_id,
                      block: selectedFollowUpTask?.unit?.block_id,
                      level: selectedFollowUpTask?.unit?.level_id,
                      unit: selectedFollowUpTask?.unit_id
                    }
                  : { project: null, block: null, level: null, unit: null }
              }
            />
          </Modal>
        )}

        {/* Possession Date Modal */}
        {showPossessionModal && (
          <Modal
            isDirty
            confirmOnClose
            width={40}
            name="Possession Date"
            onClose={() => {
              setShowPossessionModal(false);
              setPossessionDate('');
            }}>
            <div className="flex flex-col space-y-4">
              {/* Info */}
              <div className="bg-blue-50 p-4 rounded-lg mb-2">
                <h3 className="font-semibold text-lg text-gray-800">
                  Possession Date for {selectedFollowUpTask?.lead?.user?.name}
                </h3>
                <p className="text-sm text-gray-600 mt-1">
                  Project: {selectedFollowUpTask?.project?.name || 'N/A'}
                </p>
              </div>

              {/* Date Input */}
              <div className="w-full">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Select Possession Date <span className="text-red-500">*</span>
                </label>
                <input
                  type="date"
                  value={possessionDate}
                  onChange={(e) => setPossessionDate(e.target.value)}
                  min={new Date().toISOString().split('T')[0]}
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-base"
                />
              </div>

              {/* Action Buttons */}
              <div className="w-full flex justify-end items-center space-x-4 mt-6 pt-4 border-t">
                <Button
                  type="reset"
                  onClick={() => {
                    setShowPossessionModal(false);
                    setPossessionDate('');
                  }}
                  displayType="secondary">
                  Cancel
                </Button>
                <Button disabled={isSubmitting} type="button" onClick={handlePossessionDateSubmit}>
                  {isSubmitting ? 'Submitting...' : 'Submit'}
                </Button>
              </div>
            </div>
          </Modal>
        )}
      </div>

      <StageQualificationModal
        open={qualModalOpen}
        onClose={() => {
          setQualModalOpen(false);
          setPendingQualAction(null);
        }}
        leadId={selectedFollowUpTask?.lead_id}
        transition={pendingQualAction?.transition}
        leadName={selectedFollowUpTask?.lead?.user?.name || selectedFollowUpTask?.lead_name}
        targetStage={pendingQualAction?.targetStage}
        onSubmit={handleQualificationSubmitLeads}
        isSubmitting={qualSubmitting || isSubmitting}
      />
    </Layout>
  );
};

export default Leads;
